// =========================================================================
// A module hub. Route `/workflows/modules/:moduleId`.
// Demo `renderModule`, source line 3011.
//
// The bands, in the demo's order:
//
//   1. **Hero** — poster number, name, tag, which agents run it, and the
//      prev / all / next controls. The demo's `.stagehead`.
//   2. **Inside this module** — a tile per workflow step (`wfTiles`, 3689).
//   3. **Purpose** — the long-form paragraph.
//   4. **Live KPIs** — the module's `live()` figures.
//   5. **Related pages** — the `views` chips, through `routeForView`.
//   6. **What this module does** — the `items()` rows: what the operating
//      model says, where it lives, the live figure, a status pill. Every row
//      opens something.
//   7. **The module's own table**, on the two modules that have one: who pays
//      (commercial) and service promises (promise).
//   8. **In this module now** — the open jobs at this stage. A row opens the
//      job where the demo's `open()` went; its **run** link opens the case
//      trace for that job.
//   9. **Agents**, **Lineage**, **Systems**, and the next-module footer.
//
// Every figure on the page is a `LiveFigure` from the seed, resolved through
// `live-figure.ts`. Nothing here is typed in.
//
// UI layer. Fork-owned (zone C), P5a lane.
// =========================================================================

import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import {
  HelpAnchor,
  SkeletonBlock,
  SkeletonCardGrid,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '@/versions/v1/chrome-oem/AskStrip';
import { MSHORT, payFor } from '@/versions/v1/shared/oem-engine';
import { BandPill, SceneSection, SourceLink, TonePill } from '@/versions/v1/shared/oem-tiles';
import { agentLastRunText } from '@/versions/v1/shared/seed/agents';
import { MODULE_STATUS_TOKENS } from '@/versions/v1/shared/seed/modules';
import { renderFigure, renderStat } from './live-figure';
import { SectionHero } from './SectionHero';
import { SectionTile } from './SectionTile';
import { SeedGlyph } from './SeedGlyph';
import { useFigureContext } from './use-figure-context';
import { useSeedNav } from './use-seed-nav';
import { lastRunContext, openJobRows, payerRows, promiseRows } from './ModuleScene.data';
import {
  moduleAgents,
  moduleCapabilities,
  moduleFor,
  moduleNumber,
  modulePosition,
  neighbours,
  stepTiles,
  systemRows,
} from './ModuleScene.transform';

/**
 * The two modules whose figures read the Meridian service register.
 *
 * `installed` counts it and reads warranty; `commercial` prints the contract
 * and warranty behind every open job. The other eight never touch it, and
 * would otherwise pay 100 requests for nothing.
 */
const MAXIMO_MODULES: ReadonlySet<string> = new Set(['installed', 'commercial']);

export function ModuleScene() {
  const { moduleId } = useParams<{ moduleId: string }>();
  const navigate = useNavigate();

  const module = useMemo(() => moduleFor(moduleId), [moduleId]);
  const { figures, ctx, raw, loading, error } = useFigureContext(
    module ? MAXIMO_MODULES.has(module.id) : false,
  );

  const seedNav = useSeedNav(
    useMemo(
      (): Readonly<Record<string, readonly Readonly<Record<string, unknown>>[]>> =>
        ctx
          ? {
              wos: ctx.workOrders as unknown as readonly Record<string, unknown>[],
              cases: ctx.pack.cases as unknown as readonly Record<string, unknown>[],
              inv: ctx.pack.inventory as unknown as readonly Record<string, unknown>[],
              parts: ctx.pack.parts as unknown as readonly Record<string, unknown>[],
              orders: ctx.pack.orders as unknown as readonly Record<string, unknown>[],
            }
          : {},
      [ctx],
    ),
  );

  const view = useMemo(() => {
    if (!module || !figures || !ctx) return null;
    return {
      kpis: module.live.map((k) => ({
        label: k.label,
        value: renderFigure(k.value, figures),
      })),
      items: module.items.map((item) => ({
        key: item.what,
        what: item.what,
        where: item.where,
        figure: renderFigure(item.figure, figures),
        status: MODULE_STATUS_TOKENS[item.status],
        nav: item.nav,
      })),
      jobs: openJobRows(module.id, ctx.derived.jobs),
      payers:
        module.id === 'commercial' && raw
          ? payerRows(ctx.derived.jobs, ctx.workOrders, (id) => payFor(id, { maximo: raw.maximo }), (id) =>
              ctx.helpers.mxOf(id),
            )
          : [],
      promises: module.id === 'promise' ? promiseRows(figures.promises) : [],
      agents: moduleAgents(module).map((a) => ({
        ...a,
        lastRunText: agentLastRunText(
          { ...a, reads: '', produces: '', module: module.id },
          lastRunContext(figures),
        ),
      })),
    };
  }, [module, figures, ctx, raw]);

  const capabilities = useMemo(
    () => (module ? moduleCapabilities(module) : []),
    [module],
  );
  const systems = useMemo(() => (module ? systemRows(module) : []), [module]);
  const tiles = useMemo(() => (module ? stepTiles(module) : []), [module]);
  const around = useMemo(() => (module ? neighbours(module) : null), [module]);

  const openMachine = useCallback(
    (id: string) => navigate(`/machines/${encodeURIComponent(id)}`),
    [navigate],
  );

  if (!module) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody className="max-w-[900px] pt-10">
          <h1 className="text-[22px] font-semibold" style={{ color: 'var(--ink-primary)' }}>
            No such module
          </h1>
          <p className="mt-2 text-[13px]" style={{ color: 'var(--ink-secondary)' }}>
            The operating model has ten modules and <code className="mono">{moduleId}</code> is
            not one of them.
          </p>
          <button
            type="button"
            onClick={() => navigate('/workflows')}
            className="mt-4 text-[13px] font-medium"
            style={{ color: 'var(--accent)' }}
          >
            All ten modules
          </button>
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  const nn = moduleNumber(module);

  return (
    <SurfacePageLayout width="full">
      <Commentable name="ModuleScene">
        <SurfacePageBody className="@container max-w-[1320px] space-y-8 pt-6">
          {error ? (
            <div
              role="alert"
              className="rounded-xl border p-4 text-sm"
              style={{
                borderColor: 'var(--hairline)',
                background: 'var(--crit-soft)',
                color: 'var(--crit)',
              }}
            >
              The aftermarket data could not be loaded ({error.message}). The figures on this
              page are not shown rather than shown wrong.
            </div>
          ) : null}

          {/* ---------------------------------------------------------- */}
          {/* Hero                                                       */}
          {/* ---------------------------------------------------------- */}
          <Commentable name="Hero">
            <SectionHero
              colour={module.col}
              icon={module.icon}
              title={`${nn} ${module.name}`}
              tag={module.tag}
              lead={module.purpose}
              stats={view ? view.kpis.map((k) => ({ eyebrow: k.label, value: k.value, detail: '' })) : []}
              meta={
                <>
                  <span
                    className="mr-0.5 text-[9.5px] font-medium uppercase tracking-[0.07em]"
                    style={{ color: 'var(--ink-tertiary)' }}
                  >
                    Run by
                  </span>
                  {module.agents.map((name) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => navigate(`/agents?agent=${encodeURIComponent(name)}`)}
                      className="rounded-[7px] border bg-[var(--surface-2)] px-2 py-0.5 text-[11px] font-medium leading-[1.2] transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                      style={{
                        borderColor: 'var(--hairline)',
                        borderBottom: `2px solid ${module.col}`,
                        color: 'var(--ink-primary)',
                      }}
                    >
                      {name}
                    </button>
                  ))}
                  <span
                    className="ml-2.5 text-[9.5px] font-medium uppercase tracking-[0.07em]"
                    style={{ color: 'var(--ink-tertiary)' }}
                  >
                    {modulePosition(module)}
                  </span>
                </>
              }
              actions={
                <>
                  {around ? (
                    <span className="flex items-center gap-1.5">
                      <button
                        type="button"
                        title={around.prev.name}
                        onClick={() => navigate(`/workflows/modules/${around.prev.id}`)}
                        className="mono inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                        style={{ borderColor: 'var(--hairline-strong)', color: 'var(--ink-secondary)' }}
                      >
                        <ArrowLeft className="h-3 w-3" aria-hidden="true" />
                        {String(around.prev.n).padStart(2, '0')}
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate('/workflows')}
                        className="rounded-md border px-2 py-1 text-[11px] font-medium transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                        style={{ borderColor: 'var(--hairline-strong)', color: 'var(--ink-secondary)' }}
                      >
                        All modules
                      </button>
                      <button
                        type="button"
                        title={around.next.name}
                        onClick={() => navigate(`/workflows/modules/${around.next.id}`)}
                        className="mono inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                        style={{ borderColor: 'var(--hairline-strong)', color: 'var(--ink-secondary)' }}
                      >
                        {String(around.next.n).padStart(2, '0')}
                        <ArrowRight className="h-3 w-3" aria-hidden="true" />
                      </button>
                    </span>
                  ) : null}
                  <HelpAnchor doc="workflows/module" />
                </>
              }
            />
          </Commentable>

          <AskStrip view="module" />

          {/* ---------------------------------------------------------- */}
          {/* Inside this module                                         */}
          {/* ---------------------------------------------------------- */}
          <Commentable name="Steps">
            <SceneSection
              title="Inside this module"
              subtitle={`${tiles.length} step${tiles.length === 1 ? '' : 's'} — each one its own page`}
            >
              <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @4xl:grid-cols-3">
                {tiles.map((tile) => (
                  <SectionTile
                    key={tile.id}
                    colour={module.col}
                    icon={module.icon}
                    label={tile.name}
                    description={tile.tag}
                    onSelect={() => navigate(tile.to)}
                  />
                ))}
              </div>
            </SceneSection>
          </Commentable>

          {/* ---------------------------------------------------------- */}
          {/* What this module does                                      */}
          {/* ---------------------------------------------------------- */}
          <Commentable name="Items">
            <SceneSection
              title="What this module does"
              subtitle="each item from the operating model · where it lives in the platform · the live figure this morning"
              actions={
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
                    Related pages:
                  </span>
                  {module.views.map((v) => (
                    <SourceLink key={v.label} onClick={() => seedNav.dispatch(v.nav)}>
                      {v.label}
                    </SourceLink>
                  ))}
                </span>
              }
            >
              {view ? (
                <ul
                  className="overflow-hidden rounded-xl border"
                  style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                >
                  {view.items.map((item, i) => (
                    <li
                      key={item.key}
                      style={{ borderTop: i ? '1px solid var(--hairline)' : undefined }}
                    >
                      <button
                        type="button"
                        onClick={() => seedNav.dispatch(item.nav)}
                        className="grid w-full grid-cols-1 items-center gap-x-2.5 gap-y-1 px-4 py-2 text-left transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)] @3xl:grid-cols-[minmax(150px,1.1fr)_minmax(140px,1fr)_minmax(180px,1.5fr)_auto]"
                      >
                        <span
                          className="min-w-0 text-[12px] font-medium"
                          style={{ color: 'var(--ink-primary)' }}
                        >
                          {item.what}
                        </span>
                        <span
                          className="min-w-0 text-[11px]"
                          style={{ color: 'var(--ink-tertiary)' }}
                        >
                          {item.where}
                        </span>
                        <span
                          className="min-w-0 text-[11.5px]"
                          style={{ color: 'var(--ink-secondary)' }}
                        >
                          {item.figure}
                        </span>
                        <TonePill
                          className="justify-self-start @3xl:justify-self-end"
                          tone={
                            item.status.tone === 'good'
                              ? 'good'
                              : item.status.tone === 'mute'
                                ? 'flat'
                                : 'warn'
                          }
                        >
                          {item.status.label}
                        </TonePill>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <SkeletonBlock className="h-64 rounded-xl" />
              )}
            </SceneSection>
          </Commentable>

          {/* ---------------------------------------------------------- */}
          {/* The two modules with a table of their own                  */}
          {/* ---------------------------------------------------------- */}
          {module.id === 'commercial' && view && view.payers.length > 0 ? (
            <SceneSection
              title="Who pays · every open job with a machine"
              subtitle="from the warranty and the service contract on the Maximo record · exceptions from the case"
            >
              <ModuleTable
                cols={['Job', 'Machine', 'Site', 'Contract', 'Warranty', 'Entitlement', '#Value']}
                rows={view.payers.map((r) => [
                  <span key="id" className="mono text-[11.5px]">
                    {r.id}
                    <br />
                    <span style={{ color: 'var(--ink-tertiary)' }}>{r.kind}</span>
                  </span>,
                  <MachineLink key="m" id={r.machine} onSelect={openMachine} />,
                  MSHORT[r.mine as keyof typeof MSHORT] ?? r.mine,
                  r.contract,
                  r.warranty,
                  <TonePill key="p" tone={r.payerTone}>
                    {r.payer}
                  </TonePill>,
                  r.value,
                ])}
              />
            </SceneSection>
          ) : null}

          {module.id === 'promise' && view && view.promises.length > 0 ? (
            <SceneSection
              title="Service promises · every open alert"
              subtitle="parts, technician, customer window → executable date, checked against the failure window"
            >
              <ModuleTable
                cols={[
                  'Alert',
                  'Machine',
                  'Failure window',
                  'Parts',
                  'Technician',
                  'Customer window',
                  'Promise',
                ]}
                rows={view.promises.map((p) => [
                  <span key="id" className="mono text-[11.5px]">
                    {p.id}
                  </span>,
                  <span key="m" className="flex items-center gap-1.5">
                    <MachineLink id={p.machine} onSelect={openMachine} />
                    <span className="text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
                      {MSHORT[p.mine as keyof typeof MSHORT] ?? p.mine}
                    </span>
                  </span>,
                  p.failureWindow,
                  p.parts,
                  p.tech,
                  p.window,
                  <span key="v" className="flex flex-col gap-0.5">
                    <TonePill tone={p.inside ? 'good' : 'bad'}>{p.fix}</TonePill>
                    <span className="text-[10.5px]" style={{ color: 'var(--ink-tertiary)' }}>
                      {p.inside ? 'inside the window' : 'outside the window — escalate'}
                    </span>
                  </span>,
                ])}
              />
            </SceneSection>
          ) : null}

          {/* ---------------------------------------------------------- */}
          {/* In this module now                                         */}
          {/* ---------------------------------------------------------- */}
          <Commentable name="OpenWork">
            <SceneSection
              title="In this module now"
              subtitle={
                view
                  ? `${view.jobs.length} open item${view.jobs.length === 1 ? '' : 's'} · click to open`
                  : 'loading'
              }
              actions={<SourceLink onClick={() => navigate('/job-board')}>Job board</SourceLink>}
            >
              {!view ? (
                <SkeletonBlock className="h-40 rounded-xl" />
              ) : view.jobs.length === 0 ? (
                <p
                  className="rounded-xl border p-4 text-[12.5px]"
                  style={{
                    borderColor: 'var(--hairline)',
                    background: 'var(--surface-2)',
                    color: 'var(--ink-tertiary)',
                  }}
                >
                  Nothing waiting here this morning.
                </p>
              ) : (
                <ModuleTable
                  cols={['Item', 'Machine', 'Site', 'What', 'Next', 'Waiting on', '#Progress']}
                  rows={view.jobs.map((j) => [
                    <button
                      key="id"
                      type="button"
                      onClick={() => seedNav.dispatch(j.nav)}
                      className="rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                    >
                      <span
                        className="mono text-[11.5px] font-medium underline decoration-dotted underline-offset-[3px]"
                        style={{ color: 'var(--accent)' }}
                      >
                        {j.id}
                      </span>
                      <br />
                      <span className="text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
                        {j.kind}
                      </span>
                    </button>,
                    j.machine ? (
                      <MachineLink key="m" id={j.machine} onSelect={openMachine} />
                    ) : (
                      '—'
                    ),
                    MSHORT[j.mine as keyof typeof MSHORT] ?? j.mine,
                    j.title,
                    <span key="n" style={{ color: 'var(--ink-secondary)' }}>
                      {j.next}
                    </span>,
                    j.owner,
                    <span key="p" className="inline-flex items-center gap-2">
                      <TonePill tone={j.tone}>{j.pct}%</TonePill>
                      <SourceLink onClick={() => navigate(j.runHref)}>run</SourceLink>
                    </span>,
                  ])}
                  onRowSelect={(r) => seedNav.dispatch(view.jobs[r].nav)}
                  rowKeys={view.jobs.map((j) => j.key)}
                />
              )}
            </SceneSection>
          </Commentable>

          {/* ---------------------------------------------------------- */}
          {/* Agents                                                     */}
          {/* ---------------------------------------------------------- */}
          <Commentable name="Agents">
            <SceneSection
              title="Agents in this module"
              subtitle="what each one does, and when it last ran"
              actions={<SourceLink onClick={() => navigate('/agents')}>Agent roster</SourceLink>}
            >
              {view ? (
                <div className="grid grid-cols-1 gap-3 @2xl:grid-cols-2 @5xl:grid-cols-3">
                  {view.agents.map((a) => (
                    <button
                      key={a.name}
                      type="button"
                      onClick={() => navigate(`/agents?agent=${encodeURIComponent(a.name)}`)}
                      className="flex min-w-0 flex-col gap-1.5 rounded-xl border p-3.5 text-left transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                    >
                      <span className="flex items-center gap-2">
                        <span
                          aria-hidden="true"
                          className="h-2.5 w-2.5 shrink-0 rounded-sm"
                          style={{ background: a.colour }}
                        />
                        <span
                          className="min-w-0 text-[13px] font-semibold"
                          style={{ color: 'var(--ink-primary)' }}
                        >
                          {a.name}
                        </span>
                        <BandPill band={a.level === 'L2' ? 'good' : 'fair'}>
                          {a.levelLabel}
                        </BandPill>
                      </span>
                      <span
                        className="text-[11.5px] leading-[1.5]"
                        style={{ color: 'var(--ink-secondary)' }}
                      >
                        {a.description}
                      </span>
                      <span
                        className="mt-auto flex items-baseline gap-2 pt-1 text-[11px]"
                        style={{ color: 'var(--ink-tertiary)' }}
                      >
                        Last run
                        <b className="font-medium" style={{ color: 'var(--ink-secondary)' }}>
                          {a.lastRunText}
                        </b>
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <SkeletonCardGrid count={3} cols={3} />
              )}
            </SceneSection>
          </Commentable>

          {/* ---------------------------------------------------------- */}
          {/* Lineage                                                    */}
          {/* ---------------------------------------------------------- */}
          {capabilities.length > 0 ? (
            <Commentable name="Lineage">
              <SceneSection
                title="Lineage"
                subtitle="capabilities of the original aftermarket wheel that live in this module"
              >
                <div className="grid grid-cols-1 gap-3 @2xl:grid-cols-2 @5xl:grid-cols-3">
                  {capabilities.map((cap) => {
                    const stat = figures ? renderStat(cap.stat, figures) : '';
                    return (
                      <article
                        key={cap.id}
                        className="flex min-w-0 flex-col gap-1.5 rounded-xl border p-3.5"
                        style={{
                          borderColor: 'var(--hairline)',
                          background: 'var(--surface-2)',
                          borderLeft: `3px solid ${
                            cap.coverageKey === 'full' ? 'var(--band-good)' : 'var(--accent)'
                          }`,
                        }}
                      >
                        <h3 className="flex items-center gap-2 text-[13px] font-semibold">
                          <span
                            className="mono text-[11px] font-medium"
                            style={{ color: 'var(--ink-tertiary)' }}
                          >
                            {cap.n}
                          </span>
                          <span className="min-w-0" style={{ color: 'var(--ink-primary)' }}>
                            {cap.name}
                          </span>
                          <TonePill
                            className="ml-auto shrink-0"
                            tone={cap.coverageKey === 'full' ? 'good' : 'flat'}
                          >
                            {cap.coverage}
                          </TonePill>
                        </h3>
                        <p
                          className="text-[11.5px] leading-[1.45]"
                          style={{ color: 'var(--ink-tertiary)' }}
                        >
                          <b className="font-semibold">Was:</b> {cap.then}
                        </p>
                        <p
                          className="text-[12.5px] leading-[1.5]"
                          style={{ color: 'var(--ink-secondary)' }}
                        >
                          {cap.now}
                        </p>
                        <p className="text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
                          <b className="font-semibold">Agent:</b> {cap.agent}
                          {stat ? (
                            <>
                              {' · '}
                              <b className="font-semibold">Live:</b> {stat}
                            </>
                          ) : null}
                        </p>
                        {cap.links.length > 0 ? (
                          <p className="flex flex-wrap gap-x-3 gap-y-1 pt-0.5">
                            {cap.links.map((link) =>
                              link.action ? (
                                <SourceLink
                                  key={link.label}
                                  onClick={() =>
                                    seedNav.dispatch(
                                      link.openDoc
                                        ? { kind: 'openDoc', key: link.openDoc }
                                        : { kind: 'show', action: link.action! },
                                    )
                                  }
                                >
                                  {link.label}
                                </SourceLink>
                              ) : (
                                <span
                                  key={link.label}
                                  className="text-[11.5px]"
                                  style={{ color: 'var(--ink-tertiary)' }}
                                >
                                  {link.label}
                                </span>
                              ),
                            )}
                          </p>
                        ) : null}
                      </article>
                    );
                  })}
                </div>
              </SceneSection>
            </Commentable>
          ) : null}

          {/* ---------------------------------------------------------- */}
          {/* Systems                                                    */}
          {/* ---------------------------------------------------------- */}
          <Commentable name="Systems">
            <SceneSection
              title="Systems feeding this module"
              subtitle="each keeps producing what it already produces"
              actions={<SourceLink onClick={() => navigate('/connectors')}>Connectors</SourceLink>}
            >
              <ul className="grid grid-cols-1 gap-2 @xl:grid-cols-2 @4xl:grid-cols-3">
                {systems.map((s) => {
                  const body = (
                    <>
                      {s.icon ? (
                        <SeedGlyph
                          markup={s.icon}
                          className="h-4 w-4 shrink-0"
                          stroke="var(--ink-secondary)"
                          strokeWidth={1.6}
                        />
                      ) : null}
                      <span className="min-w-0">
                        <span
                          className="block truncate text-[12px] font-semibold"
                          style={{ color: 'var(--ink-primary)' }}
                        >
                          {s.name}
                        </span>
                        {s.subtitle ? (
                          <span
                            className="block truncate text-[10.5px] leading-[1.3]"
                            style={{ color: 'var(--ink-tertiary)' }}
                          >
                            {s.subtitle}
                          </span>
                        ) : null}
                      </span>
                      {s.status ? (
                        <TonePill className="ml-auto shrink-0" tone={s.tone === 'good' ? 'good' : 'flat'}>
                          {s.status}
                        </TonePill>
                      ) : null}
                    </>
                  );

                  return (
                    <li key={s.name}>
                      {s.linked ? (
                        <button
                          type="button"
                          onClick={() => navigate('/connectors')}
                          className="flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-left transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                          style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                        >
                          {body}
                        </button>
                      ) : (
                        <div
                          className="flex items-center gap-2 rounded-lg border px-2.5 py-2"
                          style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                        >
                          {body}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </SceneSection>
          </Commentable>

          {/* ---------------------------------------------------------- */}
          {/* Next module                                                */}
          {/* ---------------------------------------------------------- */}
          {around ? (
            <button
              type="button"
              onClick={() => navigate(`/workflows/modules/${around.next.id}`)}
              className="flex w-full flex-wrap items-center gap-3 rounded-xl border p-4 text-left transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
              style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
            >
              <span
                className="mono text-[20px] font-semibold"
                style={{ color: around.next.col }}
              >
                {String(around.next.n).padStart(2, '0')}
              </span>
              <span className="min-w-0">
                <span
                  className="block text-[13px] font-semibold"
                  style={{ color: 'var(--ink-primary)' }}
                >
                  {around.next.name}
                </span>
                <span className="block text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
                  {around.next.tag}
                </span>
              </span>
              <span
                className="ml-auto flex items-center gap-1.5 text-[12px] font-medium"
                style={{ color: 'var(--accent)' }}
              >
                Open {around.next.short}
                <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
            </button>
          ) : null}

          {loading && !view ? (
            <p className="sr-only" role="status">
              Loading the aftermarket data.
            </p>
          ) : null}
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}

/** `mLink(id)` — a machine id that opens the machine page. */
function MachineLink({ id, onSelect }: { id: string; onSelect: (id: string) => void }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onSelect(id);
      }}
      className="mono rounded text-[11.5px] font-medium underline decoration-dotted underline-offset-[3px] transition-colors hover:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
      style={{ color: 'var(--accent)' }}
    >
      {id}
    </button>
  );
}

/**
 * The demo's `table.t` inside a `.twrap`.
 *
 * A column label beginning `#` is right-aligned and the `#` is stripped, which
 * is `wfT`'s convention (demo line 3386) and is reused here so the module
 * tables and the workflow tables read the same.
 */
function ModuleTable({
  cols,
  rows,
  rowKeys,
  onRowSelect,
}: {
  cols: readonly string[];
  rows: readonly (readonly React.ReactNode[])[];
  rowKeys?: readonly string[];
  /**
   * Makes the whole row a mouse target, as the demo's `tr onclick` was.
   * Clicks on a control inside the row (a machine link, the run link) are
   * left to that control. Keyboard users reach the same place through the
   * row's first-cell button.
   */
  onRowSelect?: (row: number) => void;
}) {
  return (
    <div
      className="overflow-x-auto rounded-xl border"
      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
    >
      <table className="w-full border-collapse text-left">
        <thead>
          <tr>
            {cols.map((c) => (
              <th
                key={c}
                scope="col"
                className={`whitespace-nowrap px-2.5 py-[7px] text-[10.5px] font-semibold uppercase tracking-[0.06em] ${
                  c.startsWith('#') ? 'text-right' : ''
                }`}
                style={{
                  color: 'var(--ink-tertiary)',
                  borderBottom: '1px solid var(--hairline)',
                }}
              >
                {c.replace(/^#/, '')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => (
            <tr
              key={rowKeys?.[r] ?? r}
              onClick={
                onRowSelect
                  ? (e) => {
                      if ((e.target as HTMLElement).closest('button, a')) return;
                      onRowSelect(r);
                    }
                  : undefined
              }
              className={
                onRowSelect ? 'cursor-pointer transition-colors hover:bg-[var(--surface-1)]' : undefined
              }
            >
              {row.map((cell, i) => (
                <td
                  key={i}
                  className={`px-2.5 py-1.5 align-top text-[12px] ${
                    cols[i]?.startsWith('#') ? 'mono text-right' : ''
                  }`}
                  style={{
                    color: 'var(--ink-primary)',
                    borderTop: r ? '1px solid var(--hairline)' : undefined,
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ModuleScene;
